import React from "react";
import { useCaptions } from "../context/CaptionsContext";
import { useAudio } from "../context/AudioContext";
import useCaptionTranslation from "../hooks/useCaptionTranslation";

/**
 * LiveCaptions — global subtitle band for TTS-driven content.
 *
 * Read-aloud Bible verses, AI Companion replies, Storybook narration,
 * dictionary read-throughs — all of these stream through AudioContext.
 * That context already tracks `queue` (the array of verses / sentences
 * being spoken) and `currentIndex` (which one is playing right now).
 *
 * This component is mounted ONCE at App root and:
 *   • Shows nothing when the captions preference is OFF.
 *   • Shows nothing when no audio is actively playing.
 *   • Otherwise renders the current chunk of text in a high-contrast
 *     white-on-black band pinned ~88px above the bottom safe-area so
 *     it sits above the BottomNav and doesn't crowd the play controls.
 *
 * Language follow-up (2026-05-22): when the user picks a non-English
 * language we translate the caption text in real-time through
 * /api/translate (Claude fallback if MyMemory is rate-limited). We
 * keep showing the original English while the translation is in
 * flight so the band never blinks empty.
 */
export default function LiveCaptions() {
  const { on } = useCaptions();
  const { isPlaying, queue, currentIndex, meta, language } = useAudio();

  const current = (queue && queue[currentIndex]) || null;
  const rawText = current?.text || current?.content || "";
  const label = current?.label || meta?.title || "";

  // Hook MUST be called unconditionally to satisfy rules-of-hooks.
  // Pass empty string when there's no audio to avoid wasted work.
  const translatedText = useCaptionTranslation(rawText, language);

  if (!on) return null;
  if (!isPlaying) return null;
  if (!queue || queue.length === 0) return null;
  if (!current) return null;

  const text = translatedText || rawText;

  return (
    <div
      data-testid="live-captions"
      role="status"
      aria-live="polite"
      className="fixed bottom-24 md:bottom-28 left-1/2 -translate-x-1/2 z-[55] w-[min(92vw,720px)] pointer-events-none"
    >
      <div
        className="rounded-2xl px-5 py-3 md:py-4 backdrop-blur-md"
        style={{
          background: "rgba(0,0,0,0.84)",
          color: "#ffffff",
          textShadow: "0 1px 2px rgba(0,0,0,0.9)",
        }}
      >
        {label && (
          <div
            className="text-[10px] tracking-[0.28em] uppercase font-bold mb-1"
            style={{ color: "#FCD981" }}
            data-testid="live-captions-label"
          >
            {label}
          </div>
        )}
        <div
          className="text-base md:text-lg leading-snug font-medium"
          data-testid="live-captions-text"
        >
          {text}
        </div>
      </div>
    </div>
  );
}
